/**
 * ProcOps module registry — the single source of truth for the platform's
 * modules (PriceGuard, Supply Scanner, …).
 *
 * Both the home launcher (Landing.js) and the in-app navigation (App.js) are
 * derived from this list, so adding a new module is a ONE-PLACE change:
 *   1. add an entry here (card content + its own `nav` tabs + the `routes` it owns)
 *   2. add the module's <Route>s in App.js
 * The home screen shows a card per module automatically, and the top nav shows
 * only the active module's tabs.
 *
 * Fields:
 *   key      unique id (also the card test id)
 *   name     display name
 *   tagline  one-line accent subtitle
 *   desc     card body copy
 *   points   3 bullet highlights on the card
 *   accent   brand colour (CSS var or hex)
 *   mark     glyph shown in the card icon tile
 *   entry    route the card opens into (the module's landing tab)
 *   nav      top-nav tabs shown WHILE inside the module ([] = single-page module
 *            that manages its own in-page sub-tabs, e.g. Supply Scanner)
 *   routes   every path that belongs to the module — drives "which module am I
 *            in" detection (include legacy/alias routes)
 */
export const MODULES = [
  {
    key: "priceguard",
    name: "PriceGuard",
    tagline: "Price-comparison & overcharge analytics",
    desc: "Invoice vs. price-file matching, effective-dated pricing, coverage, inflation and internal-price checks across every supplier.",
    points: ["Overcharge & credit recovery", "Supplier price benchmarking", "Coverage & data quality"],
    accent: "var(--color-primary-500)",
    mark: "£",
    entry: "/dashboard",
    metrics: {
      endpoint: "/dashboard/stats",
      items: [
        { key: "total_potential_credit", label: "Potential credit", fmt: "gbp0" },
        { key: "total_invoices", label: "Invoices", fmt: "num" },
      ],
    },
    nav: [
      { name: "Dashboard", path: "/dashboard" },
      { name: "Data", path: "/data", alias: ["/invoices", "/price-files", "/upload-status"] },
      { name: "Reports", path: "/reports" },
      { name: "Recovery", path: "/credits" },
      { name: "Internal Prices", path: "/internal-price-comparison" },
      { name: "Submissions", path: "/submissions", alias: ["/portal-admin", "/records-updater"] },
      { name: "Match Review", path: "/match-review" },
      { name: "Email Log", path: "/email-log" },
    ],
    routes: [
      "/dashboard", "/data", "/invoices", "/price-files", "/upload-status",
      "/reports", "/credits", "/internal-price-comparison",
      "/submissions", "/portal-admin", "/records-updater", "/match-review", "/email-log",
    ],
  },
  {
    key: "qbr",
    name: "QBR",
    tagline: "Cost-of-install modelling & supplier review",
    desc: "Model how supplier price changes hit cost per install, review suppliers across pricing + service, and track inflation and switching savings — the quarterly-review lens.",
    points: ["Price-change → cost-per-install", "Cross-module supplier scorecards", "Inflation & switching savings"],
    accent: "var(--color-accent-violet, #a855f7)",
    mark: "Σ",
    entry: "/qbr/modelling",
    metrics: {
      endpoint: "/qbr/cost-per-install",
      items: [
        { key: "overall.avgCost", label: "Avg cost / install", fmt: "gbp0" },
        { key: "jobs", label: "Jobs analysed", fmt: "num" },
      ],
    },
    nav: [
      { name: "Price Modelling", path: "/qbr/modelling" },
      { name: "Budget Modeller", path: "/qbr/budget" },
      { name: "Scorecards", path: "/qbr/scorecards" },
      { name: "Inflation", path: "/inflation-index" },
      { name: "Benchmark", path: "/benchmark" },
    ],
    routes: [
      "/qbr", "/qbr/modelling", "/qbr/budget", "/qbr/scorecards",
      "/inflation-index", "/benchmark",
    ],
  },
  {
    key: "inventory_planner",
    name: "Inventory Planner",
    tagline: "Install forecast to part demand",
    desc: "Turns the weekly install forecast into part demand by DC and week, replacing the forecast spreadsheets, with accuracy tracking to follow.",
    points: ["Per-DC and total part forecast", "Draft / publish assumptions", "ICM-ready export"],
    accent: "#8b5cf6",
    mark: "IP",
    entry: "/ip",
    nav: [
      { name: "Overview", path: "/ip" },
      { name: "Forecast", path: "/ip/forecast" },
      { name: "Accuracy", path: "/ip/accuracy" },
      { name: "Cost & Supply", path: "/ip/cost-supply" },
      { name: "Inputs", path: "/ip/inputs" },
      { name: "Settings", path: "/ip/settings" },
    ],
    routes: ["/ip", "/ip/forecast", "/ip/accuracy", "/ip/cost-supply", "/ip/inputs", "/ip/settings"],
  },
];

/** The module that owns `pathname`, or undefined at platform level (home/settings). */
export function moduleForPath(pathname) {
  return MODULES.find((m) =>
    m.routes.some((r) => pathname === r || pathname.startsWith(r + "/")),
  );
}
